import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { apiClient, ApiError } from "@/lib/api-client";
import { authMeQueryKey } from "./useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

interface LoginForm {
  password: string;
}

export function LoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const { register, handleSubmit } = useForm<LoginForm>({
    defaultValues: { password: "" },
  });

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true);
    try {
      // 204 No Content on success; api-client returns undefined.
      await apiClient.post("/auth/login", { password: values.password });
      // Force a fresh /auth/me (the session cookie is now set) and WAIT for it
      // before navigating. invalidateQueries only marks the query stale; on the
      // /login route there's no active observer, so it would not refetch — and
      // navigating immediately let RequireAuth read the STALE
      // {authenticated:false} from cache and bounce straight back to /login (the
      // "have to log in twice" bug). refetchQueries refetches even inactive
      // queries and resolves once fresh auth state is cached.
      await queryClient.refetchQueries({ queryKey: authMeQueryKey });
      navigate("/", { replace: true });
    } catch (err) {
      const message =
        err instanceof ApiError && err.status === 401
          ? "Invalid password"
          : err instanceof Error
            ? err.message
            : "Login failed";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <div className="flex min-h-dvh items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Login</CardTitle>
          <CardDescription>Enter your password to continue.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                autoFocus
                {...register("password")}
              />
            </div>
            <Button type="submit" disabled={submitting} className="w-full">
              {submitting ? "Signing in…" : "Login"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
